import React, { useCallback, useMemo } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useNodeStore, selectSelectedNodeIds } from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import { CustomEdge as CustomEdgeType } from '@/types/edge-types';
import { CustomNode } from '@/types/node-types';
import {
  HANDLE_STYLING,
  HandleTypes,
  getWorkflowEdgeStrokeColor,
} from '@/constants/node-styling';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { isArtifactEdge } from '@/pages/workflow/helper/artifact-node-manager';
import {
  getConditionalBranchLabel,
  getSelectedCase,
  isConditionalEdge,
} from '@/pages/workflow/helper/functional-control-flow';

export function useEdgeInteraction(id: string, selected?: boolean) {
  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  const { onEdgesDelete, edges, nodes } = useNodeStore();
  const { setEdges } = useReactFlow();
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);
  const { isWorkflowLockingEnabled } = useLaunchDarkly();
  const isWorkflowLocked = isWorkflowLockingEnabled
    ? (currentWorkflow?.is_locked ?? false)
    : false;

  const currentEdge = useMemo(
    () => edges.find((e) => e.id === id) as CustomEdgeType | undefined,
    [id, edges],
  );

  const strokeColor = useMemo(() => {
    if (!currentEdge) {
      return HANDLE_STYLING[HandleTypes.PARAM].color;
    }
    const sourceNode = nodes.find((n) => n.id === currentEdge.source) as
      | CustomNode
      | undefined;
    const targetNode = nodes.find((n) => n.id === currentEdge.target) as
      | CustomNode
      | undefined;
    return getWorkflowEdgeStrokeColor(currentEdge, sourceNode, targetNode);
  }, [currentEdge, nodes]);

  const conditionalState = useMemo(() => {
    if (!currentEdge || !isConditionalEdge(currentEdge)) {
      return {
        isConditional: false,
        branchLabel: null,
        isSelectedBranch: false,
        hasSelectedBranch: false,
      };
    }
    const sourceNode = nodes.find((node) => node.id === currentEdge.source) as
      | CustomNode
      | undefined;
    const selectedCase = getSelectedCase(sourceNode?.data?.result);
    const branchLabel = getConditionalBranchLabel(currentEdge);
    return {
      isConditional: true,
      branchLabel,
      isSelectedBranch: Boolean(selectedCase) && selectedCase === branchLabel,
      hasSelectedBranch: Boolean(selectedCase),
    };
  }, [currentEdge, nodes]);

  const showDeleteToolbar = useMemo(
    () =>
      !!selected &&
      selectedNodeIds.length === 0 &&
      !isWorkflowLocked &&
      !(currentEdge ? isArtifactEdge(currentEdge) : false),
    [selected, selectedNodeIds.length, isWorkflowLocked, currentEdge],
  );

  const handleEdgeClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (isWorkflowLocked) return;
      setEdges((eds) =>
        eds.map((edge) => ({
          ...edge,
          selected: edge.id === id,
        })),
      );
    },
    [id, isWorkflowLocked, setEdges],
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isWorkflowLocked) return;
      const edgeToDelete = edges.find((e) => e.id === id);
      if (edgeToDelete) {
        onEdgesDelete([edgeToDelete as CustomEdgeType]);
      }
    },
    [id, isWorkflowLocked, edges, onEdgesDelete],
  );

  return {
    currentEdge,
    nodes,
    strokeColor,
    ...conditionalState,
    showDeleteToolbar,
    handleEdgeClick,
    handleDeleteClick,
  };
}
